import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    TibboDataType,
    ValidationMessage,
    SettingState,
    SettingOption,
    ValidationMessageType,
} from '@appblocks/codegeneration/types';
import * as micropython from './micropython';
import * as tios from './tios';
import * as zephyr from './zephyr';
import * as simulator from './simulator';


export default class Variable extends BaseFeature {
    constructor() {
        super();
        this.name = 'Variable';
        this.id = 'variable';
        this.priority = 50;
        this.singleInstance = false;

        const settings = [
            {
                id: 'VARNAME',
                name: 'Variable Name',
                settingType: SettingType.NAME,
                required: true,
                default: 'var',
            },
            {
                id: 'DATATYPE',
                name: 'Data Type',
                settingType: SettingType.OPTION,
                required: true,
                default: 'byte',
                options: [] as SettingOption[],
            },
            {
                id: 'NAME',
                name: 'Description',
                settingType: SettingType.STRING,
                required: true,
                default: '',
            },
            {
                id: 'FEATURE',
                name: 'Feature',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '',
            },
            {
                id: 'FEATURE_SETTING',
                name: 'Feature Setting ID',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '',
            },
            {
                id: 'MINIMUM',
                name: 'Minimum',
                settingType: SettingType.NUMBER,
                required: true,
                default: '',
            },
            {
                id: 'MAXIMUM',
                name: 'Maximum',
                settingType: SettingType.NUMBER,
                required: true,
                default: '',
            },
            {
                id: 'DEFAULT',
                name: 'Default Value',
                settingType: SettingType.DYNAMIC,
                required: true,
                default: '',
            },
            {
                id: 'POLL_INTERVAL',
                name: 'Update Interval (seconds)',
                settingType: SettingType.NUMBER,
                required: true,
                default: '',
                helpText: 'Determines how often variables are updated. The minimum interval is 0.001 seconds (1 millisecond); the maximum is 60 seconds.',
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getName(): string {
        const desc = this.getSettingValue('NAME');
        return `${this.getSettingValue('VARNAME')}${desc && ` (${desc})`}`;
    }

    getSettingValue(settingId: string): any {
        if (settingId === 'NAME') {
            const featureId = this.getSettingValue('FEATURE');
            if (featureId !== undefined
                && featureId !== '') {
                const feature = this.project.getFeature(featureId);
                if (feature) {
                    if (feature.id === 'stg_setting') {
                        return feature.getSettingValue('NAME');
                    }
                    return `${feature.name} (${feature.getSettingName(this.getSettingValue('FEATURE_SETTING'))})`;
                }
            }
        }
        return super.getSettingValue(settingId);
    }

    validate(): ValidationMessage[] {
        const errors: Array<ValidationMessage> = [];
        const { project } = this;
        const FEATURE = this.getSettingValue('FEATURE');
        const POLL_INTERVAL = this.getSettingValue('POLL_INTERVAL');
        if (POLL_INTERVAL !== '') {
            if (Number(POLL_INTERVAL) < 0.001 || Number(POLL_INTERVAL) > 60) {
                errors.push({
                    messageType: ValidationMessageType.UPDATE,
                    message: 'Update interval must be between 0.001 and 60 seconds',
                    featureId: this.getID(),
                    settingId: 'POLL_INTERVAL',
                });
            }
        }
        if (this.getSettingValue('FEATURE') !== undefined
            && this.getSettingValue('FEATURE') !== '') {
            const feature = project.getFeature(this.getSettingValue('FEATURE'));
            if (feature) {
                const featureName = feature.getSettingValue(this.getSettingValue('FEATURE_SETTING'));
                if (this.getSettingValue('VARNAME') !== featureName) {
                    errors.push({
                        messageType: ValidationMessageType.UPDATE,
                        message: '',
                        featureId: this.getID(),
                        settingId: 'VARNAME',
                        data: featureName,
                    });
                    errors.push({
                        messageType: ValidationMessageType.UPDATE,
                        message: '',
                        featureId: this.getID(),
                        settingId: 'NAME',
                        data: feature.name,
                    });
                }
                const settingType = feature.getSettingValue('SETTINGTYPE');
                if (settingType && this.getSettingValue('DATATYPE') !== settingType) {
                    errors.push({
                        messageType: ValidationMessageType.UPDATE,
                        message: '',
                        featureId: this.getID(),
                        settingId: 'DATATYPE',
                        data: settingType,
                    });
                }
                if (feature.id === 'stg_setting') {
                    const DEFAULT = this.getSettingValue('DEFAULT');
                    const settingDefault = feature.getSettingValue('DEFAULT');
                    if (DEFAULT !== settingDefault) {
                        errors.push({
                            messageType: ValidationMessageType.UPDATE,
                            message: 'syncing variable default value with setting default value',
                            featureId: this.getID(),
                            settingId: 'DEFAULT',
                            data: settingDefault,
                        });
                    }
                }
            } else {
                errors.push({
                    messageType: ValidationMessageType.REMOVE,
                    message: '',
                    featureId: this.getID(),
                    settingId: 'FEATURE',
                    data: '',
                });
            }
        }
        const defaultValue = this.getSettingValue('DEFAULT');
        const min = this.getSettingValue('MINIMUM');
        const max = this.getSettingValue('MAXIMUM');
        const dataType = this.getSettingValue('DATATYPE');

        if (this.getSettingValue('FEATURE_SETTING') === 'SETTINGID') {
            const settings = project.getFeature('stg').getSettingValue('SETTINGS');
            for (let i = 0; i < settings.length; i++) {
                const setting = settings[i];
                if (setting.getSettingValue('SETTINGID') === this.getSettingValue('VARNAME')) {
                    const tmpMin = setting.getSettingValue('MIN');
                    const tmpMax = setting.getSettingValue('MAX');
                    if (this.getSettingValue('MINIMUM') !== tmpMin) {
                        errors.push({
                            messageType: ValidationMessageType.UPDATE,
                            message: '',
                            featureId: this.getID(),
                            settingId: 'MINIMUM',
                            data: tmpMin,
                        });
                    }
                    if (this.getSettingValue('MAXIMUM') !== tmpMax) {
                        errors.push({
                            messageType: ValidationMessageType.UPDATE,
                            message: '',
                            featureId: this.getID(),
                            settingId: 'MAXIMUM',
                            data: tmpMax,
                        });
                    }
                }
            }
        } else {
            if (dataType !== 'string'
                && (defaultValue === '' || Number.isNaN(Number(defaultValue)))) {
                errors.push({
                    messageType: ValidationMessageType.UPDATE,
                    message: '',
                    featureId: this.getID(),
                    settingId: 'DEFAULT',
                    data: '0',
                });
            }
            if (dataType === 'string' && FEATURE !== '') {
                if (min !== '') {
                    errors.push({
                        messageType: ValidationMessageType.UPDATE,
                        message: '',
                        featureId: this.getID(),
                        settingId: 'MINIMUM',
                        data: '',
                    });
                }
                if (max !== '') {
                    errors.push({
                        messageType: ValidationMessageType.UPDATE,
                        message: '',
                        featureId: this.getID(),
                        settingId: 'MAXIMUM',
                        data: '',
                    });
                }
            }
            if (dataType === 'time' || dataType === 'datetime') {
                if (min !== '') {
                    errors.push({
                        messageType: ValidationMessageType.UPDATE,
                        message: '',
                        featureId: this.getID(),
                        settingId: 'MINIMUM',
                        data: '',
                    });
                }
                if (max !== '') {
                    errors.push({
                        messageType: ValidationMessageType.UPDATE,
                        message: '',
                        featureId: this.getID(),
                        settingId: 'MAXIMUM',
                        data: '',
                    });
                }
            }
        }
        return errors;
    }

    initSettings(project?: any, parentFeature?: any): void {
        super.initSettings(project, parentFeature, 'VARNAME');
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        if (settingId === 'DATATYPE') {
            if (this.getSettingValue('FEATURE')) {
                return [
                    {
                        name: 'Byte',
                        value: 'byte',
                    },
                    {
                        name: 'Char',
                        value: 'char',
                    },
                    {
                        name: 'Integer',
                        value: 'integer',
                    },
                    {
                        name: 'Short',
                        value: 'short',
                    },
                    {
                        name: 'Word',
                        value: 'word',
                    },
                    {
                        name: 'Long',
                        value: 'long',
                    },
                    {
                        name: 'Dword',
                        value: 'dword',
                    },
                    {
                        name: 'Real',
                        value: 'real',
                    },
                    {
                        name: 'Float',
                        value: 'float',
                    },
                    {
                        name: 'String',
                        value: 'string',
                    },
                    {
                        name: 'Boolean',
                        value: 'boolean',
                    },
                    {
                        name: 'Date Time',
                        value: 'datetime',
                    },
                    {
                        name: 'Time',
                        value: 'time',
                    },
                ];
            }
            return [
                {
                    name: 'Value(number)',
                    value: 'float',
                },
                {
                    name: 'String(text)',
                    value: 'string',
                },
                {
                    name: 'Date Time',
                    value: 'datetime',
                },
                {
                    name: 'Time',
                    value: 'time',
                },
            ];
        }
        return super.getSettingOptions(settingId);
    }

    getState(settingId: string) {
        let state = super.getState(settingId);
        const FEATURE = this.getSettingValue('FEATURE');
        if (FEATURE) {
            if (['DATATYPE', 'VARNAME', 'NAME', 'FEATURE', 'FEATURE_SETTING', 'MINIMUM', 'MAXIMUM'].includes(settingId)) {
                state = SettingState.DISABLED;
            }
        }
        const DATATYPE = this.getSettingValue('DATATYPE');
        if (DATATYPE === 'time' || DATATYPE === 'datetime') {
            if (['MINIMUM', 'MAXIMUM', 'DEFAULT'].includes(settingId)) {
                state = SettingState.HIDDEN;
            }
        }
        if (DATATYPE === 'string') {
            if (['MINIMUM', 'MAXIMUM'].includes(settingId) && FEATURE !== '') {
                state = SettingState.HIDDEN;
            }
        }
        if (settingId === 'DEFAULT') {
            // disable if linked to a setting
            const feature = this.project.getFeature(this.getSettingValue('FEATURE'));
            if (feature) {
                const setting = feature.getSetting(this.getSettingValue('FEATURE_SETTING'));
                if (setting && feature.id === 'stg_setting') {
                    state = SettingState.DISABLED;
                } else {
                    state = SettingState.ENABLED;
                }
            }
        }
        return state;
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'micropython':
                return micropython.getCodeBlocks(this);
            case 'zephyr':
                return zephyr.getCodeBlocks(this);
            case 'simulator':
                return simulator.getCodeBlocks(this);
            default:
                return tios.getCodeBlocks(this);
        }
    }

    getDetails(): string {
        return 'https://docs.tibbo.com/taiko/lang_variables_simple_types';
    }
}
